/**
 *
 * Pure data layer: walks a `LayoutFetchResponse` and the corresponding
 * `PLPageMeta` and returns a normalized `CollectionPageSchemaProps` shape that
 * the path-agnostic `<CollectionPageSchema>` component renders.
 *
 * Scope: PL CLP. Always emits CollectionPage + BreadcrumbList + ItemList, plus a
 * CollectionPage `significantLink` array — CMS-authored `seoData.significantLink[]`
 * merged with every subcollection URL from the FILTER_AND_SORT widget — whenever any
 * exist (independent of FAQ presence). When BFF surfaces a non-empty `seoData.faqData[]`,
 * additionally emits a FAQPage node + `hasPart` reference on CollectionPage.
 */
import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import type { PLPageMeta } from '@src/components/pl-page-context'
import { getProductLink } from '@src/components/product-card/product-card.helpers'
import { absolutizeCanonical } from '@src/components/seo/canonical.utils'
import { isAllSubcollectionValue } from '@src/pages/[...slug]/components/collection/utils'
import { appendQuerySuffix } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'

import type { LayoutFetchResponse, Widget } from '@lastbrand/storefront-proto-registry/api/v1/layoutfetch'
import type { FaqSeoItem } from '@lastbrand/storefront-proto-registry/api/v1/seo'
import type { ProductCardData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Cap matches Schema.org / Google's recommendation to keep ItemList lightweight.
// Centralizing it here so a future BFF extractor can reuse the same number.
export const MAX_ITEM_LIST_PRODUCTS = 20

export interface CollectionPageSchemaProduct {
  name: string
  url: string
}

export interface CollectionPageSchemaBreadcrumb {
  name: string
  url?: string
}

export interface CollectionPageSchemaFaq {
  question: string
  answer: string
}

export interface CollectionPageSchemaProps {
  pageUrl: string
  pageName: string
  description: string
  aboutName: string
  numberOfItems: number
  products: CollectionPageSchemaProduct[]
  breadcrumbs: CollectionPageSchemaBreadcrumb[]
  /**
   * FAQ Q&A pairs sourced from BFF `seoData.faqData[]`. Empty array when
   * FAQ data is absent — the renderer treats empty-array and undefined the
   * same way (no FAQPage / hasPart / significantLink emitted).
   */
  faqs: CollectionPageSchemaFaq[]
  /**
   * Absolute URLs emitted as CollectionPage `significantLink`: CMS-authored
   * `seoData.significantLink[]` merged with every subcollection URL from the
   * FILTER_AND_SORT widget, de-duplicated. Independent of FAQ presence.
   */
  significantLinks: string[]
}

// The PL `LayoutFetchResponse.widgets[]` array is flat — `WidgetChild` does
// not nest further widgets — so a single linear lookup is all that's needed
// regardless of CLP vs subcollection.
const findWidget = (widgets: readonly Widget[] = [], type: WidgetType): Widget | undefined =>
  widgets.find(widget => widget.type === type)

const buildProducts = (
  productListWidget: Widget | undefined,
  absoluteUrl: (path: string) => string,
): { products: CollectionPageSchemaProduct[]; numberOfItems: number } => {
  const children = productListWidget?.children ?? []
  const products: CollectionPageSchemaProduct[] = []

  // Match the existing PL ProductGridWidget: don't filter on `child.type`,
  // just take any child that carries `productItem` data. Keeps us resilient
  // to enum drift in the proto registry.
  for (const child of children) {
    if (products.length >= MAX_ITEM_LIST_PRODUCTS) break
    const card: ProductCardData | undefined = child.data?.productItem
    // Guard on the raw card slug, not the built link: getProductLink() always
    // returns at least "/" (+ color param), so a card with an empty url would
    // otherwise slip through and emit a bogus base-URL ListItem.
    const rawSlug = card?.cardVariants[0]?.url?.split('?')[0] ?? ''
    const name = card?.title
    if (!rawSlug || !name) continue
    const slug = getProductLink(rawSlug, card.cardVariants[0]?.displayConfig?.value)
    products.push({ name, url: absoluteUrl(slug) })
  }

  // Prefer the widget-reported total when paginated; otherwise the number of
  // cards we actually surfaced (capped by MAX_ITEM_LIST_PRODUCTS).
  const numberOfItems = productListWidget?.pagination?.total ?? products.length
  return { products, numberOfItems }
}

const buildBreadcrumbs = (
  breadcrumbsWidget: Widget | undefined,
  absoluteUrl: (path: string) => string,
): CollectionPageSchemaBreadcrumb[] => {
  const items = breadcrumbsWidget?.data?.breadCrumbsData?.breadCrumbs ?? []

  return items
    .filter(item => item.name.trim())
    .map((item, index, arr) => {
      const isLast = index === arr.length - 1
      return {
        name: item.name,
        ...(isLast || !item.slug ? {} : { url: absoluteUrl(item.slug) }),
      }
    })
}

const buildFaqs = (faqData: FaqSeoItem[] | undefined): CollectionPageSchemaFaq[] => {
  if (!Array.isArray(faqData)) return []
  const out: CollectionPageSchemaFaq[] = []
  for (const entry of faqData) {
    const question = entry.question.trim()
    const answer = entry.answer.trim()
    if (!question || !answer) continue
    out.push({ question, answer })
  }
  return out
}

// significantLink values authored on the BFF can arrive as bare-domain strings
// (e.g. `www.quince.com/...`), site-relative paths, or fully-qualified URLs.
// Normalise to absolute URLs so the schema we publish doesn't depend on the
// crawler's URL-resolution behaviour.
const absolutizeSignificantLink = (raw: string, absoluteUrl: (path: string) => string): string | null => {
  const trimmed = raw.trim()
  if (!trimmed) return null
  if (/^https:\/\//i.test(trimmed)) return trimmed
  // Upgrade plain-HTTP CMS values to https so we never publish http:// in schema.
  if (/^http:\/\//i.test(trimmed)) return trimmed.replace(/^http:\/\//i, 'https://')
  if (trimmed.startsWith('//')) return `https:${trimmed}`
  if (/^www\./i.test(trimmed)) return `https://${trimmed}`
  return absoluteUrl(trimmed.startsWith('/') ? trimmed : `/${trimmed}`)
}

const buildSignificantLinks = (links: string[] | undefined, absoluteUrl: (path: string) => string): string[] => {
  if (!Array.isArray(links)) return []
  const out: string[] = []
  for (const link of links) {
    if (typeof link !== 'string') continue
    const absolute = absolutizeSignificantLink(link, absoluteUrl)
    if (absolute) out.push(absolute)
  }
  return out
}

const buildSubcollectionLinks = (
  filterSortWidget: Widget | undefined,
  absoluteUrl: (path: string) => string,
): string[] => {
  const options = filterSortWidget?.data?.filterSortData?.subcollections?.options
  if (!options?.length) return []
  const out: string[] = []
  for (const option of options) {
    if (isAllSubcollectionValue(option.id) || isAllSubcollectionValue(option.slug)) continue
    const slug = option.slug.trim()
    if (!slug) continue
    const absolute = absoluteUrl(slug.startsWith('/') ? slug : `/${slug}`)
    if (absolute) out.push(absolute)
  }
  return out
}

const buildDescription = (args: {
  firstFaqAnswer?: string
  seoDescription?: string
  pageName: string
  fallbackWithName: string
  fallbackDefault: string
}): string => {
  const { firstFaqAnswer, seoDescription, pageName, fallbackWithName, fallbackDefault } = args
  if (firstFaqAnswer?.trim()) return firstFaqAnswer.trim()
  if (seoDescription?.trim()) return seoDescription.trim()
  return pageName ? fallbackWithName : fallbackDefault
}

const buildPageUrl = (pageMeta: PLPageMeta): string => {
  const base =
    absolutizeCanonical(pageMeta.seoData?.canonicalUrl) ?? absolutizeCanonical(pageMeta.pathname || '/') ?? ''
  if (!base) return ''
  // Self-referential to the filtered view, matching the page-level canonical + og:url. Gated on
  // isV2Format (the authoritative flag); appendQuerySuffix no-ops when the suffix is absent.
  if (!pageMeta.isV2Format) return base
  return appendQuerySuffix(base, pageMeta.canonicalFilterSuffix)
}

export const resolveSchemaPageName = (pageMeta: Pick<PLPageMeta, 'seoData'>): string => pageMeta.seoData?.title ?? ''

/**
 * Build path-agnostic schema props from a PL `LayoutFetchResponse`.
 *
 * Returns `null` when there isn't enough data to emit a meaningful @graph
 * (no products and no breadcrumbs) so the caller can short-circuit rendering.
 *
 * `pageName` is caller-provided (typically `resolveSchemaPageName(pageMeta)`)
 * so the builder stays deterministic and the fallback chain has exactly one
 * implementation. `fallbacks` carries pre-translated description copy; the
 * builder stays locale-agnostic — the renderer resolves the strings via
 * i18next and passes them in. See `CollectionPagePLRendererContent` for the
 * wiring.
 */
export interface CollectionPageSchemaDescriptionFallbacks {
  /** Pre-translated string used when a pageName is available. May contain interpolated copy. */
  withName: string
  /** Pre-translated string used when no pageName is available. */
  default: string
}

export const buildCollectionPageSchemaPropsPL = (
  plData: LayoutFetchResponse,
  pageMeta: PLPageMeta,
  pageName: string,
  fallbacks: CollectionPageSchemaDescriptionFallbacks,
): CollectionPageSchemaProps | null => {
  const widgets = plData.widgets
  const absoluteUrl = (path: string): string => absolutizeCanonical(path) ?? ''

  const { products, numberOfItems } = buildProducts(findWidget(widgets, WidgetType.PRODUCT_LIST), absoluteUrl)
  const breadcrumbs = buildBreadcrumbs(findWidget(widgets, WidgetType.BREADCRUMBS), absoluteUrl)
  const faqs = buildFaqs(pageMeta.seoData?.faqData)
  const significantLinks = Array.from(
    new Set([
      ...buildSignificantLinks(pageMeta.seoData?.significantLink, absoluteUrl),
      ...buildSubcollectionLinks(findWidget(widgets, WidgetType.FILTER_AND_SORT), absoluteUrl),
    ]),
  )

  const description = buildDescription({
    firstFaqAnswer: faqs[0]?.answer,
    seoDescription: pageMeta.seoData?.description,
    pageName,
    fallbackWithName: fallbacks.withName,
    fallbackDefault: fallbacks.default,
  })
  const pageUrl = buildPageUrl(pageMeta)

  // Defensive: if we don't have a page URL or any breadcrumbs/products, there's
  // nothing useful to emit and we'd just publish noise to crawlers.
  if (!pageUrl) return null
  if (products.length === 0 && breadcrumbs.length === 0) return null

  return {
    pageUrl,
    pageName,
    description,
    aboutName: pageName,
    numberOfItems,
    products,
    breadcrumbs,
    faqs,
    significantLinks,
  }
}
